/**
 * Capacity cell roster.
 *
 * The service breakdown + children roster for one group on one date, extracted verbatim
 * from `CapacityOverviewDialog`'s body so it can be hosted in EITHER:
 * - the non-modal detail Sheet (capacity page, C3), or
 * - the modal `CapacityOverviewDialog` (shift coverage — `shifts/CoverageCard.tsx`).
 *
 * This is a content move only: the roster-first re-layout is CAP-3/C12. It renders only
 * when mounted, so the queries below need no `open` gate — the previous `enabled: open`
 * is equivalent to "mounted" (Radix does not render closed Dialog/Sheet content).
 */

import { useState, useMemo } from 'react';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ChevronDown, ChevronUp, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useBillingSettings } from '@/hooks/useBilling';
import { useServiceBreakdown } from '@/hooks/useCapacity';
import { ServiceBreakdownSection } from './ServiceBreakdownSection';
import { AllocationChildListItem } from './AllocationChildListItem';
import { translateService } from '@/lib/serviceTranslations';
import type { CalendarChildView } from '@/types/calendar';
import { useGroupDayAllocations } from '@/hooks/useGroupDayAllocations';
import { useMoveDragContext } from '@/contexts/MoveDragContext';
import { Skeleton } from '@/components/ui/skeleton';

export interface CapacityCellRosterProps {
  groupId: string;
  /**
   * Kept on the public API for parity with CapacityOverviewDialog's props: the host
   * (dialog title / sheet header) owns the heading, so the roster body itself never
   * renders the group name.
   */
  groupName?: string;
  date: string;
  onChildClick?: (childId: string) => void;
  /** Pre-fetched children data. When provided, skips the API call. */
  children?: CalendarChildView[];
  /** Pre-computed flag for service breakdown availability. */
  hasServiceBreakdownHint?: boolean;
  /** Pre-computed peak service code. */
  peakServiceHint?: string;
  /** CAP-4c / C18 — opens the single-child move flow. Additive; the roster only forwards it. */
  onMoveRequest?: (childId: string, currentGroupId: string) => void;
}

export function CapacityCellRoster({
  groupId,
  date,
  onChildClick,
  children: childrenProp,
  hasServiceBreakdownHint,
  peakServiceHint,
  onMoveRequest,
}: CapacityCellRosterProps) {
  const { i18n } = useLingui();
  // The week-view roster is a drag SOURCE as well as a drop target. Read from
  // the context rather than a prop: surfaces without drag-and-drop wired up
  // (CapacityOverviewDialog, shifts/CoverageCard) get the inert default and
  // behave exactly as before, with nothing to thread through.
  const { enabled: dragEnabled } = useMoveDragContext();
  // C12: the children roster is now the primary, default-visible content, so it opens by default.
  const [isChildrenExpanded, setIsChildrenExpanded] = useState(true);
  const { data: billingSettings } = useBillingSettings();
  const serviceDefinitions = useMemo(
    () => billingSettings?.services ?? [],
    [billingSettings?.services],
  );

  // Fetch children data on-demand if not provided via props
  const { data: fetchedData, isLoading: isLoadingChildren } = useGroupDayAllocations(
    groupId,
    date,
    { enabled: !childrenProp },
  );

  // Use prop data or fetched data, sorted alphabetically by first name
  const children = useMemo(() => {
    const raw = childrenProp ?? fetchedData?.children ?? [];
    return [...raw].sort((a, b) => {
      const nameA = String(a.name || '').trim();
      const nameB = String(b.name || '').trim();
      return nameA.localeCompare(nameB);
    });
  }, [childrenProp, fetchedData?.children]);
  const hasServiceBreakdown = hasServiceBreakdownHint ?? fetchedData?.hasServiceBreakdown ?? false;

  const { data: serviceBreakdown } = useServiceBreakdown(groupId, date);
  const hasServiceBreakdownFromHook = serviceBreakdown
    ? serviceBreakdown.services && serviceBreakdown.services.length > 0
    : hasServiceBreakdown;

  const peakService = serviceBreakdown
    ? serviceBreakdown.services?.find((s) => s.service_code === serviceBreakdown.peak_service)
    : undefined;

  // C12: lead with the peak figure so "how busy is this cell, and when" is the first thing read,
  // rather than a wall of near-identical service rows.
  const peakSummary = useMemo(() => {
    const code = peakServiceHint ?? peakService?.service_code;
    if (!code || !peakService) return null;
    const percent = Math.round(
      Math.max(
        peakService.headcount_utilization_percent ?? 0,
        peakService.weighted_utilization_percent ?? 0,
      ),
    );
    return {
      name: translateService(code, serviceDefinitions, i18n),
      headcount: peakService.headcount ?? 0,
      maxHeadcount: peakService.max_headcount ?? 0,
      percent,
    };
  }, [peakServiceHint, peakService, serviceDefinitions, i18n]);

  const isLoading = !childrenProp && isLoadingChildren;

  return (
    <div className="mt-4">
      {/* Peak summary — the focal figure, first (C12). */}
      {peakSummary && (
        <div
          className="mb-3 flex items-center gap-2 text-sm"
          data-testid="roster-peak-summary"
        >
          <Star className="h-3.5 w-3.5 text-warning-on-surface fill-current flex-shrink-0" aria-hidden="true" />
          <span className="font-medium">
            <Trans>Peak</Trans>: {peakSummary.name}
          </span>
          <span className="text-muted-foreground tabular-nums">
            {peakSummary.headcount}
            {peakSummary.maxHeadcount > 0 ? `/${peakSummary.maxHeadcount}` : ''} ({peakSummary.percent}%)
          </span>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-3 py-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 p-2">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Children List Section */}
      {!isLoading && children.length === 0 && (
        <div className="grid grid-cols-[minmax(120px,auto)_minmax(100px,auto)_1fr_minmax(200px,auto)_minmax(200px,auto)_24px] items-center gap-2 py-2 px-2">
          <p className="text-sm text-muted-foreground col-span-5 text-left">
            <Trans>No children allocated to this group on this date</Trans>
          </p>
        </div>
      )}

      {!isLoading && children.length > 0 && (
        <Collapsible open={isChildrenExpanded} onOpenChange={setIsChildrenExpanded}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full h-8 px-2">
              <div className="w-full grid grid-cols-[minmax(120px,auto)_minmax(100px,auto)_1fr_minmax(200px,auto)_minmax(200px,auto)_24px] items-center gap-2">
                <span className="text-sm text-left">
                  {isChildrenExpanded ? (
                    <>
                      <Trans>Hide Children</Trans> ({children.length})
                    </>
                  ) : (
                    <>
                      <Trans>Show Children</Trans> ({children.length})
                    </>
                  )}
                </span>
                <div />
                <div />
                <div />
                <div />
                <div className="flex items-center justify-center">
                  {isChildrenExpanded ? (
                    <ChevronUp className="h-4 w-4 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 flex-shrink-0" />
                  )}
                </div>
              </div>
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-2 space-y-1.5">
            {children.map((child) => {
              const nameStr = String(child.name || '').trim();
              const [firstname, ...lastnameParts] = nameStr.split(/\s+/);
              return (
                <AllocationChildListItem
                  key={child.id}
                  childId={child.id}
                  services={child.services}
                  serviceDefinitions={serviceDefinitions}
                  ageTypeCode={child.ageTypeCode}
                  isAbsent={child.isAbsent}
                  isExtraDaycare={child.isExtraDaycare}
                  hasOverride={child.hasOverride}
                  absenceReason={child.absenceReason}
                  absenceIsFullDay={child.absenceIsFullDay}
                  birthdate={child.birthdate}
                  agesOutOn={child.agesOutOn}
                  referenceDate={date}
                  allocationFromDate={child.allocationFromDate}
                  allocationToDate={child.allocationToDate}
                  isJoiningThisDay={child.isJoiningThisDay}
                  isLeavingThisDay={child.isLeavingThisDay}
                  onClick={onChildClick ? () => onChildClick(child.id) : undefined}
                  onMoveRequest={
                    onMoveRequest ? (id) => onMoveRequest(id, groupId) : undefined
                  }
                  currentGroupId={groupId}
                  draggable={dragEnabled}
                  childData={{
                    firstname: firstname || '',
                    lastname: lastnameParts.join(' ') || '',
                    avatarUrl: child.avatarUrl,
                    childInternalId: child.childInternalId,
                  }}
                />
              );
            })}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Full nine-service matrix — moved BELOW the roster and default-collapsed (C12), so
          unused (0%) service rows never push the children below the fold. All data that was in
          the dialog stays reachable behind this expander (§5). */}
      {hasServiceBreakdownFromHook && (
        <div className="mt-3 border-t pt-3">
          <ServiceBreakdownSection
            groupId={groupId}
            date={date}
            peakService={peakServiceHint ?? peakService?.service_code}
            defaultExpanded={false}
          />
        </div>
      )}
    </div>
  );
}
